/** The itinerary workspace's context (plan §5, D10). The reads over the page draft — one
 *  row (`useOutlineNode`), one path (`useOutlineField`), a derived value of the whole draft
 *  (`useDraftSelector`), the setter and the structural runner — are the records outline's
 *  (`records/outline/store.tsx`), with per-record subscriptions, so editing stop A never
 *  re-renders stop B. `ItineraryProvider` gives them the tour's outline (its baselines, its
 *  levels and membership lists, the mode) and the workspace what is the tour's own: the
 *  tenant, the tour's dates, the issues store, the "open activity row" bus.
 *
 *  Both carry only what does not change per keystroke: the baseline (memoised on the
 *  tour's `updated_ts`), the mode, whether the operator may edit, the tenant. */
import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { RecordKey } from '@/features/records/draft/changeSet';
import type { OutlineIssues } from '@/features/records/outline/issues';
import { OutlineProvider, type OutlineContextValue } from '@/features/records/outline/store';

import { MEMBERSHIP_PATHS } from '../record/collections';
import type { TourBaseline } from '../record/baselines';

import { TOUR_OUTLINE } from './model';

export interface ItineraryContextValue {
  baseline: TourBaseline;
  tourRec: RecordKey;
  /** Edit mode: views read the draft. */
  editing: boolean;
  /** The operator may change the itinerary (console_tours_manage, an owning operator, not
   *  deleted); with `editing` the inputs are live. */
  canEdit: boolean;
  /** Why not, when `canEdit` is false. */
  lockReason: string | null;
  tenantId: number;
  tourId: number;
  /** A fixed-date tour (days carry calendar dates). */
  isBespoke: boolean;
  startDate: string | null;
  /** The page's issues store (the rules' and the server's messages per input). */
  issues: OutlineIssues;
  /** Asks a stop's activity list to open one row (a ledger row focusing its field). */
  expand: ExpandBus;
}

/** "Open activity row <id>" — the workspace emits, the selected stop's list listens. */
export class ExpandBus {
  private listeners = new Set<(activityId: number) => void>();
  private last: number | null = null;

  listen(fn: (activityId: number) => void): () => void {
    this.listeners.add(fn);
    // A list mounted by the same request (the stop was just selected) still hears it.
    if (this.last !== null) fn(this.last);
    return () => {
      this.listeners.delete(fn);
    };
  }

  emit(activityId: number): void {
    this.last = activityId;
    for (const l of [...this.listeners]) l(activityId);
    window.setTimeout(() => {
      if (this.last === activityId) this.last = null;
    }, 0);
  }
}

const ItineraryContext = createContext<ItineraryContextValue | null>(null);

/** The workspace's context and, under it, the records outline's over the tour. */
export function ItineraryProvider({ value, children }: { value: ItineraryContextValue; children: ReactNode }) {
  const { baseline, tourRec, editing, canEdit } = value;
  const outline = useMemo<OutlineContextValue>(
    () => ({ baselines: baseline.paths, ownerRec: tourRec, spec: TOUR_OUTLINE, memberships: MEMBERSHIP_PATHS, editing, canEdit }),
    [baseline.paths, tourRec, editing, canEdit],
  );
  return (
    <ItineraryContext.Provider value={value}>
      <OutlineProvider value={outline}>{children}</OutlineProvider>
    </ItineraryContext.Provider>
  );
}

export function useItinerary(): ItineraryContextValue {
  const v = useContext(ItineraryContext);
  if (!v) throw new Error('useItinerary must be used inside <ItineraryProvider>');
  return v;
}
